// ── Applications.js — Premium Student Application Manager ───────────────────
// Master tracking table · Advanced filters · Full student profiles ·
// Test scores · Account & portal access · Cloud document folders ·
// University-level tracking with filter by university / program / status
//
// Data split:
//   leads collection        → base student record (name, phone, email, counselor…)
//   companies/{id}/studentProfiles/{leadId} → all extended fields below

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { db, storage } from '../firebase/config';
import {
  collection, query, where, onSnapshot, setDoc,
  doc, serverTimestamp, addDoc, updateDoc,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// ── Drive helpers ─────────────────────────────────────────────────────────────
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function parseFolderUrl(url) {
  if (!url) return null;
  if (url.includes('drive.google.com')) {
    const m = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    return m ? { provider: 'google', folderId: m[1], folderUrl: url } : null;
  }
  if (url.includes('onedrive.live.com') || url.includes('sharepoint.com') || url.includes('1drv.ms')) {
    return { provider: 'microsoft', folderId: null, folderUrl: url };
  }
  return null;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const UNI_STATUSES = [
  { id: 'shortlisted', label: 'Shortlisted',       color: '#94a3b8', bg: '#f1f5f9' },
  { id: 'applied',     label: 'Applied',            color: '#f59e0b', bg: '#fffbeb' },
  { id: 'under_review',label: 'Under Review',       color: '#3b82f6', bg: '#eff6ff' },
  { id: 'interview',   label: 'Interview',          color: '#8b5cf6', bg: '#f5f3ff' },
  { id: 'conditional', label: 'Conditional Offer',  color: '#f97316', bg: '#fff7ed' },
  { id: 'waitlisted',  label: 'Waitlisted',         color: '#eab308', bg: '#fefce8' },
  { id: 'accepted',    label: 'Accepted ✓',         color: '#10b981', bg: '#ecfdf5' },
  { id: 'rejected',    label: 'Rejected',           color: '#ef4444', bg: '#fef2f2' },
  { id: 'enrolled',    label: 'Enrolled 🎓',        color: '#7c3aed', bg: '#ede9fe' },
  { id: 'withdrawn',   label: 'Withdrawn',          color: '#6b7280', bg: '#f9fafb' },
];

const VISA_STATUSES = [
  'Not Applicable', 'Not Applied', 'In Preparation', 'Applied',
  'Biometrics Pending', 'Interview Scheduled', 'Approved ✓', 'Rejected',
];

const DEGREE_OPTIONS = [
  "Bachelor's (UG)", "Master's (PG)", 'PhD', 'MBA', 'MIM', 'MS', 'MA',
  'JD', 'MD', 'LLM', 'MFA', 'MEng', 'Diploma / PGDip', 'Associate', 'Certificate',
];

const SEMESTER_OPTIONS = [
  'Fall 2024', 'Spring 2025', 'Summer 2025', 'Fall 2025',
  'Spring 2026', 'Summer 2026', 'Fall 2026', 'Spring 2027',
];

const MAIN_COUNSELORS = ['Rajesh', 'Sudhir', 'Richa', 'Harshita', 'Mukesh', 'Vani', 'Other'];

const COUNTRY_OPTIONS = [
  'USA', 'UK', 'Canada', 'Australia', 'Germany', 'Netherlands', 'France',
  'Ireland', 'New Zealand', 'Singapore', 'UAE', 'Switzerland', 'Sweden', 'Other',
];

// ── Sub-components ─────────────────────────────────────────────────────────────

/** Inline status pill with dropdown */
function UniStatusPill({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const cur = UNI_STATUSES.find(s => s.id === status) || UNI_STATUSES[0];
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span onClick={() => setOpen(o => !o)}
        style={{ ...stStyle(cur), cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
        {cur.label} ▾
      </span>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 999, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', minWidth: 170, marginTop: 4 }}>
          {UNI_STATUSES.map(s => (
            <div key={s.id} onClick={() => { onChange(s.id); setOpen(false); }}
              style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: s.color, cursor: 'pointer', background: s.id === status ? s.color + '12' : 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = s.color + '0e'}
              onMouseLeave={e => e.currentTarget.style.background = s.id === status ? s.color + '12' : 'transparent'}>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function stStyle(s) {
  return { color: s.color, background: s.bg || s.color + '18', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 };
}

/** Masked field — shows ••••• by default, reveal on toggle */
function SecureField({ value, onChange, placeholder, style }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        className="crm-input"
        type={show ? 'text' : 'password'}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ flex: 1, fontSize: 12, ...style }}
      />
      <button onClick={() => setShow(s => !s)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: '4px 6px' }}>
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  );
}

/** Section card with collapsible header */
function Section({ title, icon, children, badge, defaultOpen = true, accent }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', background: accent ? accent + '08' : 'transparent', borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: accent || 'var(--text-primary)' }}>{title}</span>
          {badge != null && <span style={{ background: 'rgba(124,58,237,0.1)', color: '#7c3aed', borderRadius: 99, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{badge}</span>}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
      </div>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

/** 2-column form grid helper */
function Grid({ children, cols = 2 }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10 }}>{children}</div>;
}

/** Labelled input field */
function Field({ label, children, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : {}}>
      <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function Inp({ value, onChange, placeholder, type = 'text', style }) {
  return <input className="crm-input" type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ fontSize: 12, ...style }} />;
}

function Sel({ value, onChange, options, placeholder }) {
  return (
    <select className="crm-select" value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: '100%', fontSize: 12 }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── Master Table ───────────────────────────────────────────────────────────────
function MasterTable({ students, onSelect, selectedId, loading }) {
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState(1);

  const sorted = useMemo(() => {
    return [...students].sort((a, b) => {
      let va = '', vb = '';
      if (sortCol === 'name')    { va = a.name || ''; vb = b.name || ''; }
      if (sortCol === 'counsel') { va = a.profile?.mainCounselor || ''; vb = b.profile?.mainCounselor || ''; }
      if (sortCol === 'visa')    { va = a.profile?.visaStatus || ''; vb = b.profile?.visaStatus || ''; }
      if (sortCol === 'uni')     { va = a.profile?.universityFinalized || ''; vb = b.profile?.universityFinalized || ''; }
      return va.localeCompare(vb) * sortDir;
    });
  }, [students, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => -d);
    else { setSortCol(col); setSortDir(1); }
  };

  const th = (label, col, w) => (
    <th onClick={col ? () => toggleSort(col) : undefined}
      style={{ padding: '10px 12px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap', background: '#f8fafc', borderBottom: '1px solid var(--border)', cursor: col ? 'pointer' : 'default', width: w, position: 'sticky', top: 0, zIndex: 10 }}>
      {label}{col && sortCol === col ? (sortDir === 1 ? ' ↑' : ' ↓') : ''}
    </th>
  );

  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading students…</div>;
  if (!students.length) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 32 }}>🎓</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No students match your filters</div>
      <div style={{ fontSize: 12 }}>Try adjusting your filters or search terms</div>
    </div>
  );

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1400 }}>
        <thead>
          <tr>
            {th('#', null, 40)}
            {th('Student Name', 'name', 180)}
            {th('Main', 'counsel', 80)}
            {th('Test Score', null, 110)}
            {th('Counselor', null, 100)}
            {th('Applied', null, 70)}
            {th('Accepted', null, 80)}
            {th('Rejected', null, 70)}
            {th('Finalized University', 'uni', 200)}
            {th('Degree / Major', null, 160)}
            {th('Semester', null, 90)}
            {th('Visa', 'visa', 110)}
            {th('Scholarship', null, 110)}
            {th('School', null, 140)}
            {th('Partner', null, 70)}
            {th('Student ID', null, 100)}
            {th('Review', null, 65)}
            {th('📸', null, 45)}
            {th('🎬', null, 45)}
            {th('Email', null, 170)}
            {th('Parent', null, 140)}
            {th('Folder', null, 65)}
            {th('Meeting', null, 65)}
            {th('Actions', null, 80)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => {
            const p = s.profile || {};
            const unis = p.universities || [];
            const accepted = unis.filter(u => u.status === 'accepted' || u.status === 'enrolled').length;
            const rejected = unis.filter(u => u.status === 'rejected').length;
            const testScore = p.sat?.length
              ? `SAT ${p.sat[p.sat.length - 1]?.total || '—'}`
              : p.gre?.total ? `GRE ${p.gre.total}` : p.gmat?.total ? `GMAT ${p.gmat.total}` : p.act?.composite ? `ACT ${p.act.composite}` : '—';
            const isActive = selectedId === s.id;

            return (
              <tr key={s.id} onClick={() => onSelect(s)}
                style={{ background: isActive ? 'rgba(124,58,237,0.06)' : i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.04)' }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(124,58,237,0.03)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'; }}>
                <td style={td}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 700, color: isActive ? '#7c3aed' : 'var(--text-primary)', maxWidth: 180 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getLeadName(s)}</div>
                  {p.dob && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>DOB: {p.dob}</div>}
                </td>
                <td style={td}>{p.mainCounselor || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ ...td, color: '#7c3aed', fontWeight: 700, fontSize: 11 }}>{testScore}</td>
                <td style={td}>{p.counselorName || s.assignedTo || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{unis.length || '—'}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#10b981' }}>{accepted || '—'}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{rejected || '—'}</td>
                <td style={{ ...td, maxWidth: 200 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{p.universityFinalized || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}</div>
                </td>
                <td style={{ ...td, maxWidth: 160 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[p.degree, p.major].filter(Boolean).join(' · ') || <span style={{ color: 'var(--text-muted)' }}>—</span>}</div>
                </td>
                <td style={{ ...td, fontSize: 10 }}>{p.semesterApplied || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={td}>
                  {p.visaStatus ? (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: p.visaStatus.includes('Approved') ? '#ecfdf5' : p.visaStatus.includes('Rejected') ? '#fef2f2' : '#f1f5f9', color: p.visaStatus.includes('Approved') ? '#10b981' : p.visaStatus.includes('Rejected') ? '#ef4444' : '#64748b' }}>{p.visaStatus}</span>
                  ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>
                <td style={td}>{p.scholarshipTotal ? <span style={{ color: '#10b981', fontWeight: 700 }}>{p.scholarshipTotal}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ ...td, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.schoolName || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ ...td, textAlign: 'center' }}>{p.partnerSchool ? '✓' : '—'}</td>
                <td style={td}>{p.studentIdFinal || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ ...td, textAlign: 'center' }}>{p.googleReview ? '⭐' : '—'}</td>
                <td style={{ ...td, textAlign: 'center' }}>{p.hasPic ? '📸' : '—'}</td>
                <td style={{ ...td, textAlign: 'center' }}>{p.hasVideo ? '🎬' : '—'}</td>
                <td style={{ ...td, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{p.appEmail || s.email || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ ...td, fontSize: 11 }}>
                  {(p.fatherName || p.motherName) ? [p.fatherName, p.motherName].filter(Boolean)[0] : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  {p.documentFolderUrl ? (
                    <a href={p.documentFolderUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      style={{ fontSize: 16, textDecoration: 'none' }}>📁</a>
                  ) : '—'}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  {p.meetingLink ? (
                    <a href={p.meetingLink} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      style={{ fontSize: 16, textDecoration: 'none' }}>📹</a>
                  ) : '—'}
                </td>
                <td style={td}>
                  <button onClick={e => { e.stopPropagation(); onSelect(s); }}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, background: 'rgba(124,58,237,0.1)', color: '#7c3aed', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                    View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const td = { padding: '10px 12px', fontSize: 12, color: 'var(--text-secondary)', verticalAlign: 'middle', borderBottom: '1px solid rgba(0,0,0,0.04)' };

// ── Compact Document Row (inline upload within Applications profile) ───────────
function CompactDocRow({ docItem, driveConfig, companyId, leadId, currentUser }) {
  const [uploading, setUploading] = useState(false);
  const [driveErr, setDriveErr] = useState('');
  const fileRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setDriveErr('');

    if (driveConfig) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('companyId', companyId);
        fd.append('userId', currentUser?.uid || currentUser?.id || '');
        fd.append('folderId', driveConfig.folderId || '');
        fd.append('folderUrl', driveConfig.folderUrl || '');
        fd.append('docName', file.name);
        fd.append('provider', driveConfig.provider);
        const resp = await fetch(`${BACKEND_URL}/api/drive/upload`, { method: 'POST', body: fd });
        const data = await resp.json();
        if (!resp.ok || data.error) throw new Error(data.error || 'Drive upload failed');
        await updateDoc(doc(db, 'companies', companyId, 'leads', leadId, 'documents', docItem.id), {
          status: 'uploaded', fileUrl: data.fileUrl, fileId: data.fileId,
          fileName: file.name, storageProvider: data.provider, storagePath: null,
          uploadedAt: serverTimestamp(), uploadedBy: currentUser?.id,
        });
      } catch (err) { setDriveErr(err.message); }
      finally { setUploading(false); }
      return;
    }
    // Firebase Storage fallback
    const path = `documents/${companyId}/${leadId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed', null,
      (err) => { console.error(err); setUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        await updateDoc(doc(db, 'companies', companyId, 'leads', leadId, 'documents', docItem.id), {
          status: 'uploaded', fileUrl: url, fileName: file.name,
          storagePath: path, storageProvider: 'firebase',
          uploadedAt: serverTimestamp(), uploadedBy: currentUser?.id,
        });
        setUploading(false);
      }
    );
  };

  const ST_COLOR = { pending: '#f59e0b', requested: '#0ea5e9', uploaded: '#8b5cf6', verified: '#10b981', rejected: '#ef4444' };
  const ST_LABEL = { pending: 'Pending', requested: 'Requested', uploaded: 'Uploaded', verified: 'Verified ✓', rejected: 'Rejected' };
  const st = docItem.status || 'pending';
  const provColor = driveConfig?.provider === 'google' ? '#16a34a' : driveConfig?.provider === 'microsoft' ? '#2563eb' : '#7c3aed';
  const provLabel = driveConfig?.provider === 'google' ? '🟢 Upload' : driveConfig?.provider === 'microsoft' ? '🔵 Upload' : '⬆ Upload';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>
        {st === 'verified' ? '✅' : st === 'rejected' ? '❌' : st === 'uploaded' ? '📄' : '📋'}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {docItem.name}
        </div>
        {docItem.fileName && (
          <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 1 }}>
            {docItem.storageProvider === 'google' ? '🟢' : docItem.storageProvider === 'microsoft' ? '🔵' : '📎'}{' '}
            <a href={docItem.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>{docItem.fileName}</a>
          </div>
        )}
        {driveErr && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>⚠️ {driveErr}</div>}
        {uploading && <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 2 }}>⏳ Uploading to {driveConfig?.provider === 'google' ? 'Google Drive' : driveConfig?.provider === 'microsoft' ? 'OneDrive' : 'storage'}…</div>}
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: (ST_COLOR[st] || '#94a3b8') + '18', color: ST_COLOR[st] || '#94a3b8', flexShrink: 0 }}>
        {ST_LABEL[st] || st}
      </span>
      {(st === 'pending' || st === 'requested' || st === 'rejected') && (
        <>
          <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleUpload} />
          <button onClick={() => { setDriveErr(''); fileRef.current?.click(); }} disabled={uploading}
            style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${provColor}`, background: provColor + '14', color: provColor, cursor: uploading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {provLabel}
          </button>
        </>
      )}
    </div>
  );
}

// ── Student Profile Drawer ─────────────────────────────────────────────────────
function StudentDrawer({ student, profile: initProfile, onClose, onSave }) {
  const { currentUser, company } = useCRM();
  const [p, setP] = useState(initProfile || {});
  const [saving, setSaving] = useState(false);
  const [showPortalAccess, setShowPortalAccess] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const saveTimer = useRef(null);

  // ── Document management state ─────────────────────────────────────────
  const [docs, setDocs] = useState([]);
  const [connectedProvider, setConnectedProvider] = useState(null);
  const [showAddDocInline, setShowAddDocInline] = useState(false);
  const [newDocName, setNewDocName] = useState('');

  // Subscribe to documents subcollection for this student
  useEffect(() => {
    if (!student?.id || !company?.id) { setDocs([]); return; }
    return onSnapshot(
      collection(db, 'companies', company.id, 'leads', student.id, 'documents'),
      snap => setDocs(snap.docs.map(d => ({ ...d.data(), id: d.id })))
    );
  }, [student?.id, company?.id]);

  // Check if current counselor has a connected drive account
  useEffect(() => {
    if (!currentUser?.uid || !company?.id) return;
    const tryProvider = (provider) =>
      onSnapshot(
        doc(db, 'companies', company.id, 'connectedAccounts', `${currentUser.uid}_${provider}`),
        snap => { if (snap.exists()) setConnectedProvider(prev => prev || provider); }
      );
    const u1 = tryProvider('google');
    const u2 = tryProvider('microsoft');
    return () => { u1(); u2(); };
  }, [currentUser?.uid, company?.id]);

  // Drive folder config derived from profile
  const driveConfig = useMemo(() => parseFolderUrl(p.documentFolderUrl), [p.documentFolderUrl]);

  const addDocToList = async (name) => {
    if (!company?.id || !student?.id || !name.trim()) return;
    await addDoc(collection(db, 'companies', company.id, 'leads', student.id, 'documents'), {
      name: name.trim(), status: 'requested',
      leadId: student.id, companyId: company.id,
      createdAt: serverTimestamp(), requestedBy: currentUser?.id,
    });
  };

  useEffect(() => { setP(initProfile || {}); }, [initProfile, student?.id]);

  const update = useCallback((path, value) => {
    setP(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    // Debounced save
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaving(true);
      // Will be called via onSave callback
    }, 1500);
  }, []);

  const [shareMsg, setShareMsg] = useState('');

  const save = async () => {
    setSaving(true);
    await onSave(student.id, p);
    setSaving(false);
  };

  // Generate a secure token, store it, copy the form link to clipboard
  const handleShareForm = async () => {
    if (!company?.id || !student?.id) return;
    try {
      const token = crypto.randomUUID();
      await setDoc(
        doc(db, 'companies', company.id, 'studentProfiles', student.id),
        { formToken: token },
        { merge: true }
      );
      const url = `${window.location.origin}/student-form/${company.id}/${student.id}/${token}`;
      await navigator.clipboard.writeText(url);
      setShareMsg('✅ Link copied!');
      setTimeout(() => setShareMsg(''), 3000);
    } catch (err) {
      setShareMsg('⚠️ ' + err.message);
      setTimeout(() => setShareMsg(''), 4000);
    }
  };

  // University helpers
  const addUni = () => {
    const unis = [...(p.universities || []), { name: '', country: 'USA', program: '', degree: '', status: 'shortlisted', scholarship: '', notes: '', portalUrl: '', portalUsername: '', portalAccess: '', studentId: '' }];
    setP(prev => ({ ...prev, universities: unis }));
  };
  const updateUni = (idx, field, val) => {
    const unis = [...(p.universities || [])];
    unis[idx] = { ...unis[idx], [field]: val };
    setP(prev => ({ ...prev, universities: unis }));
  };
  const removeUni = (idx) => {
    const unis = (p.universities || []).filter((_, i) => i !== idx);
    setP(prev => ({ ...prev, universities: unis }));
  };

  // SAT helpers
  const addSat = () => setP(prev => ({ ...prev, sat: [...(prev.sat || []), { date: '', verbal: '', math: '', total: '' }] }));
  const updateSat = (idx, field, val) => {
    const sat = [...(p.sat || [])];
    sat[idx] = { ...sat[idx], [field]: val };
    if (field === 'verbal' || field === 'math') {
      const v = parseInt(sat[idx].verbal) || 0;
      const m = parseInt(sat[idx].math) || 0;
      sat[idx].total = v + m || '';
    }
    setP(prev => ({ ...prev, sat }));
  };

  // AP helpers
  const addAp = () => setP(prev => ({ ...prev, ap: [...(prev.ap || []), { subject: '', date: '', score: '' }] }));
  const updateAp = (idx, f, v) => {
    const ap = [...(p.ap || [])]; ap[idx] = { ...ap[idx], [f]: v };
    setP(prev => ({ ...prev, ap }));
  };
  const removeAp = (idx) => setP(prev => ({ ...prev, ap: (prev.ap || []).filter((_, i) => i !== idx) }));

  const SECTIONS = [
    { id: 'personal',    label: 'Personal',    icon: '👤' },
    { id: 'parents',     label: 'Parents',     icon: '👨‍👩‍👧' },
    { id: 'academic',    label: 'Academic',    icon: '🏫' },
    { id: 'account',     label: 'App Account', icon: '📧' },
    { id: 'tests',       label: 'Test Scores', icon: '📊' },
    { id: 'universities',label: 'Universities',icon: '🎓' },
    { id: 'portals',     label: 'Portal Access',icon: '🔒' },
    { id: 'documents',   label: 'Documents',   icon: '📁' },
    { id: 'tracking',    label: 'Tracking',    icon: '📋' },
  ];

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Section navigation */}
      <div style={{ width: 140, borderRight: '1px solid var(--border)', background: '#f8fafc', flexShrink: 0, overflowY: 'auto', padding: '8px 0' }}>
        {SECTIONS.map(s => (
          <div key={s.id} onClick={() => {
            setActiveSection(s.id);
            document.getElementById('drawer-section-' + s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
            style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, background: activeSection === s.id ? 'rgba(124,58,237,0.08)' : 'transparent', borderLeft: activeSection === s.id ? '3px solid #7c3aed' : '3px solid transparent', fontSize: 12, fontWeight: activeSection === s.id ? 700 : 400, color: activeSection === s.id ? '#7c3aed' : 'var(--text-secondary)' }}>
            <span>{s.icon}</span>{s.label}
          </div>
        ))}
      </div>

      {/* Detail content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{getLeadName(student)}</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {student?.email && <span style={{ marginRight: 12 }}>✉️ {student.email}</span>}
              {student?.phone && <span>📱 {student.phone}</span>}
            </div>
            {p.studentFilledAt && (
              <div style={{ fontSize: 10, color: '#10b981', fontWeight: 700, marginTop: 3 }}>
                ✅ Student self-filled on {(p.studentFilledAt?.toDate ? p.studentFilledAt.toDate() : new Date(p.studentFilledAt)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {shareMsg && (
              <span style={{ fontSize: 11, fontWeight: 700, color: shareMsg.startsWith('✅') ? '#10b981' : '#ef4444', whiteSpace: 'nowrap' }}>{shareMsg}</span>
            )}
            <button onClick={handleShareForm} title="Generate a shareable link for the student to fill in their details"
              style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.25)', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
              🔗 Share Form
            </button>
            <button onClick={save}
              style={{ padding: '8px 18px', borderRadius: 8, background: saving ? '#94a3b8' : '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              {saving ? 'Saving…' : 'Save All'}
            </button>
            <button onClick={onClose}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>✕</button>
          </div>
        </div>

        {/* ── PERSONAL ─── */}
        <div id="drawer-section-personal">
          <Section title="Personal Details" icon="👤">
            <Grid>
              <Field label="Date of Birth"><Inp value={p.dob} onChange={v => update('dob', v)} type="date" /></Field>
              <Field label="Gender">
                <Sel value={p.gender} onChange={v => update('gender', v)} options={['Male', 'Female', 'Non-binary', 'Prefer not to say']} placeholder="Select" />
              </Field>
              <Field label="Nationality"><Inp value={p.nationality} onChange={v => update('nationality', v)} placeholder="e.g. Indian" /></Field>
              <Field label="Passport Number"><Inp value={p.passportNo} onChange={v => update('passportNo', v)} placeholder="Optional" /></Field>
              <Field label="Address" full>
                <textarea className="crm-input" rows={2} value={p.address || ''} onChange={e => update('address', e.target.value)} placeholder="Full residential address" style={{ resize: 'vertical', fontSize: 12 }} />
              </Field>
            </Grid>
          </Section>
        </div>

        {/* ── PARENTS ─── */}
        <div id="drawer-section-parents">
          <Section title="Parent / Guardian Details" icon="👨‍👩‍👧">
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', marginBottom: 8 }}>Father / Guardian 1</div>
              <Grid>
                <Field label="Name"><Inp value={p.fatherName} onChange={v => update('fatherName', v)} placeholder="Father's name" /></Field>
                <Field label="Phone"><Inp value={p.fatherPhone} onChange={v => update('fatherPhone', v)} placeholder="+91 ..." /></Field>
                <Field label="Email" full><Inp value={p.fatherEmail} onChange={v => update('fatherEmail', v)} placeholder="father@email.com" /></Field>
                <Field label="Occupation"><Inp value={p.fatherOccupation} onChange={v => update('fatherOccupation', v)} placeholder="Optional" /></Field>
                <Field label="Relationship">
                  <Sel value={p.fatherRelation || 'Father'} onChange={v => update('fatherRelation', v)} options={['Father', 'Guardian', 'Uncle/Aunt', 'Other']} />
                </Field>
              </Grid>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#ec4899', marginBottom: 8 }}>Mother / Guardian 2</div>
              <Grid>
                <Field label="Name"><Inp value={p.motherName} onChange={v => update('motherName', v)} placeholder="Mother's name" /></Field>
                <Field label="Phone"><Inp value={p.motherPhone} onChange={v => update('motherPhone', v)} placeholder="+91 ..." /></Field>
                <Field label="Email" full><Inp value={p.motherEmail} onChange={v => update('motherEmail', v)} placeholder="mother@email.com" /></Field>
                <Field label="Occupation"><Inp value={p.motherOccupation} onChange={v => update('motherOccupation', v)} placeholder="Optional" /></Field>
                <Field label="Relationship">
                  <Sel value={p.motherRelation || 'Mother'} onChange={v => update('motherRelation', v)} options={['Mother', 'Guardian', 'Uncle/Aunt', 'Other']} />
                </Field>
              </Grid>
            </div>
          </Section>
        </div>

        {/* ── ACADEMIC ─── */}
        <div id="drawer-section-academic">
          <Section title="Academic Background" icon="🏫">
            <Grid>
              <Field label="High School / College Name" full>
                <Inp value={p.schoolName} onChange={v => update('schoolName', v)} placeholder="Name of school or college" />
              </Field>
              <Field label="Board / System">
                <Sel value={p.board} onChange={v => update('board', v)} options={['CBSE', 'ICSE', 'IB (IBDP)', 'Cambridge (IGCSE/A-Levels)', 'State Board', 'US High School', 'Other']} placeholder="Select board" />
              </Field>
              <Field label="GPA / Percentage"><Inp value={p.gpa} onChange={v => update('gpa', v)} placeholder="e.g. 9.2 CGPA / 92%" /></Field>
              <Field label="Degree Applying For">
                <Sel value={p.degree} onChange={v => update('degree', v)} options={DEGREE_OPTIONS} placeholder="Select degree" />
              </Field>
              <Field label="Major / Field of Study"><Inp value={p.major} onChange={v => update('major', v)} placeholder="e.g. Computer Science" /></Field>
              <Field label="Target Semester">
                <Sel value={p.semesterApplied} onChange={v => update('semesterApplied', v)} options={SEMESTER_OPTIONS} placeholder="Select semester" />
              </Field>
              <Field label="Main Counselor (Head)">
                <Sel value={p.mainCounselor} onChange={v => update('mainCounselor', v)} options={MAIN_COUNSELORS} placeholder="Select" />
              </Field>
              <Field label="Assigned Counselor"><Inp value={p.counselorName} onChange={v => update('counselorName', v)} placeholder="Counselor handling day-to-day" /></Field>
            </Grid>
          </Section>
        </div>

        {/* ── APPLICATION ACCOUNT ─── */}
        <div id="drawer-section-account">
          <Section title="Application Account" icon="📧" accent="#3b82f6">
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 0, marginBottom: 12 }}>
              Dedicated email account created for university applications (Common App, UCAS, direct portals, etc.)
            </p>
            <Grid>
              <Field label="Application Email" full>
                <Inp value={p.appEmail} onChange={v => update('appEmail', v)} placeholder="e.g. studentname2025@gmail.com" />
              </Field>
              <Field label="Account Access" full>
                <SecureField value={p.appAccess} onChange={v => update('appAccess', v)} placeholder="Account access details" />
              </Field>
              <Field label="Recovery Email"><Inp value={p.appRecoveryEmail} onChange={v => update('appRecoveryEmail', v)} placeholder="Backup / recovery email" /></Field>
              <Field label="Recovery Phone"><Inp value={p.appRecoveryPhone} onChange={v => update('appRecoveryPhone', v)} placeholder="Recovery mobile" /></Field>
            </Grid>
          </Section>
        </div>

        {/* ── TEST SCORES ─── */}
        <div id="drawer-section-tests">
          <Section title="Test Scores & Platform Details" icon="📊">
            {/* TOEFL */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0ea5e9', marginBottom: 8 }}>TOEFL</div>
              <Grid>
                <Field label="Test Date"><Inp value={p.toefl?.date} onChange={v => update('toefl.date', v)} type="date" /></Field>
                <Field label="Total Score"><Inp value={p.toefl?.score} onChange={v => update('toefl.score', v)} placeholder="e.g. 112" /></Field>
                <Field label="Reading"><Inp value={p.toefl?.reading} onChange={v => update('toefl.reading', v)} placeholder="0–30" /></Field>
                <Field label="Listening"><Inp value={p.toefl?.listening} onChange={v => update('toefl.listening', v)} placeholder="0–30" /></Field>
                <Field label="Speaking"><Inp value={p.toefl?.speaking} onChange={v => update('toefl.speaking', v)} placeholder="0–30" /></Field>
                <Field label="Writing"><Inp value={p.toefl?.writing} onChange={v => update('toefl.writing', v)} placeholder="0–30" /></Field>
                <Field label="ETS Account Username"><Inp value={p.toefl?.username} onChange={v => update('toefl.username', v)} placeholder="ETS platform username" /></Field>
                <Field label="ETS Account Access">
                  <SecureField value={p.toefl?.access} onChange={v => update('toefl.access', v)} placeholder="Access details" />
                </Field>
              </Grid>
            </div>

            {/* IELTS */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', marginBottom: 8 }}>IELTS</div>
              <Grid>
                <Field label="Test Date"><Inp value={p.ielts?.date} onChange={v => update('ielts.date', v)} type="date" /></Field>
                <Field label="Overall Band"><Inp value={p.ielts?.overall} onChange={v => update('ielts.overall', v)} placeholder="e.g. 7.5" /></Field>
                <Field label="Listening"><Inp value={p.ielts?.listening} onChange={v => update('ielts.listening', v)} placeholder="e.g. 8.0" /></Field>
                <Field label="Reading"><Inp value={p.ielts?.reading} onChange={v => update('ielts.reading', v)} placeholder="e.g. 7.0" /></Field>
                <Field label="Writing"><Inp value={p.ielts?.writing} onChange={v => update('ielts.writing', v)} placeholder="e.g. 7.0" /></Field>
                <Field label="Speaking"><Inp value={p.ielts?.speaking} onChange={v => update('ielts.speaking', v)} placeholder="e.g. 7.5" /></Field>
                <Field label="British Council / IDP Username"><Inp value={p.ielts?.username} onChange={v => update('ielts.username', v)} placeholder="Platform username" /></Field>
                <Field label="Platform Access">
                  <SecureField value={p.ielts?.access} onChange={v => update('ielts.access', v)} placeholder="Access details" />
                </Field>
              </Grid>
            </div>

            {/* SAT — multi-attempt */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>SAT (All Attempts)</div>
                <button onClick={addSat} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #f59e0b', background: '#fffbeb', color: '#b45309', cursor: 'pointer', fontWeight: 700 }}>+ Add Attempt</button>
              </div>
              {(p.sat || []).map((s, i) => (
                <div key={i} style={{ background: '#fafafa', border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>Attempt {i + 1}</div>
                  <Grid cols={4}>
                    <Field label="Date"><Inp value={s.date} onChange={v => updateSat(i, 'date', v)} type="date" /></Field>
                    <Field label="Reading & Writing"><Inp value={s.verbal} onChange={v => updateSat(i, 'verbal', v)} placeholder="200–800" /></Field>
                    <Field label="Math"><Inp value={s.math} onChange={v => updateSat(i, 'math', v)} placeholder="200–800" /></Field>
                    <Field label="Total">
                      <div style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{s.total || '—'}</div>
                    </Field>
                  </Grid>
                </div>
              ))}
              <Grid>
                <Field label="Collegeboard Username"><Inp value={p.sat_platform?.username} onChange={v => update('sat_platform.username', v)} placeholder="Collegeboard username" /></Field>
                <Field label="Collegeboard Access">
                  <SecureField value={p.sat_platform?.access} onChange={v => update('sat_platform.access', v)} placeholder="Access details" />
                </Field>
              </Grid>
            </div>

            {/* ACT */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>ACT</div>
              <Grid>
                <Field label="Test Date"><Inp value={p.act?.date} onChange={v => update('act.date', v)} type="date" /></Field>
                <Field label="Composite"><Inp value={p.act?.composite} onChange={v => update('act.composite', v)} placeholder="1–36" /></Field>
                <Field label="English"><Inp value={p.act?.english} onChange={v => update('act.english', v)} placeholder="1–36" /></Field>
                <Field label="Math"><Inp value={p.act?.math} onChange={v => update('act.math', v)} placeholder="1–36" /></Field>
                <Field label="Reading"><Inp value={p.act?.reading} onChange={v => update('act.reading', v)} placeholder="1–36" /></Field>
                <Field label="Science"><Inp value={p.act?.science} onChange={v => update('act.science', v)} placeholder="1–36" /></Field>
              </Grid>
            </div>

            {/* GRE */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginBottom: 8 }}>GRE</div>
              <Grid>
                <Field label="Test Date"><Inp value={p.gre?.date} onChange={v => update('gre.date', v)} type="date" /></Field>
                <Field label="Verbal (V)"><Inp value={p.gre?.verbal} onChange={v => update('gre.verbal', v)} placeholder="130–170" /></Field>
                <Field label="Quantitative (Q)"><Inp value={p.gre?.quant} onChange={v => update('gre.quant', v)} placeholder="130–170" /></Field>
                <Field label="AWA"><Inp value={p.gre?.awa} onChange={v => update('gre.awa', v)} placeholder="0–6" /></Field>
                <Field label="Total V+Q"><Inp value={p.gre?.total} onChange={v => update('gre.total', v)} placeholder="260–340" /></Field>
              </Grid>
            </div>

            {/* GMAT */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>GMAT</div>
              <Grid>
                <Field label="Test Date"><Inp value={p.gmat?.date} onChange={v => update('gmat.date', v)} type="date" /></Field>
                <Field label="Total Score"><Inp value={p.gmat?.total} onChange={v => update('gmat.total', v)} placeholder="200–800" /></Field>
                <Field label="Verbal"><Inp value={p.gmat?.verbal} onChange={v => update('gmat.verbal', v)} placeholder="0–60" /></Field>
                <Field label="Quantitative"><Inp value={p.gmat?.quant} onChange={v => update('gmat.quant', v)} placeholder="0–60" /></Field>
                <Field label="IR"><Inp value={p.gmat?.ir} onChange={v => update('gmat.ir', v)} placeholder="1–8" /></Field>
                <Field label="AWA"><Inp value={p.gmat?.awa} onChange={v => update('gmat.awa', v)} placeholder="0–6" /></Field>
              </Grid>
            </div>

            {/* AP */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0891b2' }}>AP Subjects</div>
                <button onClick={addAp} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #0891b2', background: '#ecfeff', color: '#0891b2', cursor: 'pointer', fontWeight: 700 }}>+ Add Subject</button>
              </div>
              {(p.ap || []).map((a, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 60px 32px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <Inp value={a.subject} onChange={v => updateAp(i, 'subject', v)} placeholder="AP Subject" />
                  <Inp value={a.date} onChange={v => updateAp(i, 'date', v)} type="date" />
                  <Inp value={a.score} onChange={v => updateAp(i, 'score', v)} placeholder="1–5" />
                  <button onClick={() => removeAp(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16 }}>✕</button>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* ── UNIVERSITIES ─── */}
        <div id="drawer-section-universities">
          <Section title="Universities Applied" icon="🎓" badge={(p.universities || []).length}>
            <div style={{ marginBottom: 10 }}>
              <button onClick={addUni}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, background: 'rgba(124,58,237,0.08)', color: '#7c3aed', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8, cursor: 'pointer' }}>
                + Add University
              </button>
            </div>

            {(p.universities || []).map((u, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid var(--border)', gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-primary)', flex: 1 }}>{u.name || `University ${i + 1}`}</span>
                  <UniStatusPill status={u.status} onChange={v => updateUni(i, 'status', v)} />
                  <button onClick={() => removeUni(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13 }}>✕</button>
                </div>
                <div style={{ padding: 12 }}>
                  <Grid>
                    <Field label="University Name" full><Inp value={u.name} onChange={v => updateUni(i, 'name', v)} placeholder="e.g. MIT" /></Field>
                    <Field label="Country">
                      <Sel value={u.country} onChange={v => updateUni(i, 'country', v)} options={COUNTRY_OPTIONS} placeholder="Country" />
                    </Field>
                    <Field label="Program / Course"><Inp value={u.program} onChange={v => updateUni(i, 'program', v)} placeholder="e.g. MS Computer Science" /></Field>
                    <Field label="Degree Level">
                      <Sel value={u.degree} onChange={v => updateUni(i, 'degree', v)} options={DEGREE_OPTIONS} placeholder="Degree" />
                    </Field>
                    <Field label="Intake / Start"><Inp value={u.intake} onChange={v => updateUni(i, 'intake', v)} placeholder="e.g. Fall 2025" /></Field>
                    <Field label="Deadline"><Inp value={u.deadline} onChange={v => updateUni(i, 'deadline', v)} type="date" /></Field>
                    <Field label="Annual Tuition"><Inp value={u.tuition} onChange={v => updateUni(i, 'tuition', v)} placeholder="e.g. $55,000" /></Field>
                    <Field label="Scholarship Offered"><Inp value={u.scholarship} onChange={v => updateUni(i, 'scholarship', v)} placeholder="e.g. $10,000 / Full" /></Field>
                    <Field label="Student ID (if admitted)"><Inp value={u.studentId} onChange={v => updateUni(i, 'studentId', v)} placeholder="University student ID" /></Field>
                    <Field label="Partner School?">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!u.partnerSchool} onChange={e => updateUni(i, 'partnerSchool', e.target.checked)} />
                        Yes, partner institution
                      </label>
                    </Field>
                    <Field label="Notes" full>
                      <textarea className="crm-input" rows={2} value={u.notes || ''} onChange={e => updateUni(i, 'notes', e.target.value)} placeholder="Any notes about this application…" style={{ resize: 'vertical', fontSize: 12 }} />
                    </Field>
                  </Grid>
                </div>
              </div>
            ))}

            {(p.universities || []).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Field label="University Finalized (Student Enrolling)" full>
                  <Sel value={p.universityFinalized} onChange={v => update('universityFinalized', v)}
                    options={(p.universities || []).map(u => u.name).filter(Boolean)}
                    placeholder="— Select finalized university —" />
                </Field>
              </div>
            )}
          </Section>
        </div>

        {/* ── PORTAL ACCESS ─── */}
        <div id="drawer-section-portals">
          <Section title="University Portal & Account Access" icon="🔒" accent="#ef4444">
            <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.12)', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 3 }}>🔒 Secure — Counselors Only</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Access details for university application portals, score report platforms and Common App. Used internally to manage and track submissions on behalf of the student.</div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <button onClick={() => setShowPortalAccess(s => !s)}
                style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, background: showPortalAccess ? 'rgba(239,68,68,0.1)' : 'rgba(0,0,0,0.05)', color: showPortalAccess ? '#dc2626' : 'var(--text-secondary)', border: '1px solid ' + (showPortalAccess ? 'rgba(239,68,68,0.3)' : 'var(--border)'), borderRadius: 8, cursor: 'pointer' }}>
                {showPortalAccess ? '🙈 Hide All Access Details' : '👁️ Show Access Details'}
              </button>
            </div>

            {/* Common App */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0ea5e9', marginBottom: 8 }}>Common App</div>
              <Grid>
                <Field label="Username / Email"><Inp value={p.commonApp?.username} onChange={v => update('commonApp.username', v)} placeholder="Common App email" /></Field>
                <Field label="Access Code">
                  {showPortalAccess ? <Inp value={p.commonApp?.access} onChange={v => update('commonApp.access', v)} placeholder="Access details" /> : <div style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>•••••••••</div>}
                </Field>
              </Grid>
            </div>

            {/* UCAS */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', marginBottom: 8 }}>UCAS (UK Applications)</div>
              <Grid>
                <Field label="Username / Personal ID"><Inp value={p.ucas?.username} onChange={v => update('ucas.username', v)} placeholder="UCAS username" /></Field>
                <Field label="Access Code">
                  {showPortalAccess ? <Inp value={p.ucas?.access} onChange={v => update('ucas.access', v)} placeholder="Access details" /> : <div style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>•••••••••</div>}
                </Field>
              </Grid>
            </div>

            {/* Per-university portals from universities array */}
            {(p.universities || []).filter(u => u.name).map((u, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>{u.name} — Application Portal</div>
                <Grid>
                  <Field label="Portal URL"><Inp value={u.portalUrl} onChange={v => updateUni(i, 'portalUrl', v)} placeholder="https://apply.university.edu" /></Field>
                  <Field label="Username"><Inp value={u.portalUsername} onChange={v => updateUni(i, 'portalUsername', v)} placeholder="Username / Email" /></Field>
                  <Field label="Access Code" full>
                    {showPortalAccess ? <Inp value={u.portalAccess} onChange={v => updateUni(i, 'portalAccess', v)} placeholder="Access details" /> : <div style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>•••••••••</div>}
                  </Field>
                </Grid>
              </div>
            ))}
          </Section>
        </div>

        {/* ── DOCUMENTS ─── */}
        <div id="drawer-section-documents">
          <Section title="Documents & Cloud Folder" icon="📁" accent="#f59e0b">

            {/* Drive account connection status banner */}
            {connectedProvider ? (
              <div style={{ background: connectedProvider === 'google' ? '#f0fdf4' : '#eff6ff', border: `1px solid ${connectedProvider === 'google' ? '#86efac' : '#93c5fd'}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>{connectedProvider === 'google' ? '🟢' : '🔵'}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: connectedProvider === 'google' ? '#15803d' : '#1d4ed8' }}>
                    {connectedProvider === 'google' ? 'Google Drive Connected' : 'OneDrive Connected'}
                  </div>
                  <div style={{ fontSize: 10, color: connectedProvider === 'google' ? '#166534' : '#1e40af' }}>
                    Your work account drive is linked — uploaded files will be saved directly to your drive folder
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>No Drive Account Connected</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Connect your Google or Microsoft account in the <strong>Meetings tab</strong> to enable direct drive uploads</div>
                </div>
              </div>
            )}

            {/* Folder URL + Meeting Link */}
            <Grid>
              <Field label="📁 Student's Drive Folder URL" full>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Inp value={p.documentFolderUrl} onChange={v => update('documentFolderUrl', v)} placeholder="https://drive.google.com/drive/folders/… or OneDrive link" style={{ flex: 1 }} />
                  {p.documentFolderUrl && (
                    <a href={p.documentFolderUrl} target="_blank" rel="noreferrer"
                      style={{ padding: '8px 14px', background: driveConfig ? (driveConfig.provider === 'google' ? '#f0fdf4' : '#eff6ff') : 'rgba(245,158,11,0.1)', color: driveConfig ? (driveConfig.provider === 'google' ? '#15803d' : '#1d4ed8') : '#b45309', borderRadius: 8, border: `1px solid ${driveConfig ? (driveConfig.provider === 'google' ? '#86efac' : '#93c5fd') : 'rgba(245,158,11,0.25)'}`, fontSize: 12, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      {driveConfig?.provider === 'google' ? '🟢' : driveConfig?.provider === 'microsoft' ? '🔵' : '📂'} Open ↗
                    </a>
                  )}
                </div>
                {driveConfig ? (
                  <div style={{ fontSize: 10, color: driveConfig.provider === 'google' ? '#15803d' : '#1d4ed8', marginTop: 4 }}>
                    {driveConfig.provider === 'google' ? '🟢 Google Drive folder linked — uploads go directly here' : '🔵 OneDrive folder linked — uploads go directly here'}
                  </div>
                ) : p.documentFolderUrl ? (
                  <div style={{ fontSize: 10, color: '#ef4444', marginTop: 4 }}>⚠️ URL not recognised as Google Drive or OneDrive</div>
                ) : (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Create a folder for this student in your drive, then paste the sharing link here</div>
                )}
              </Field>
              <Field label="Meeting / Call Recording Link" full>
                <Inp value={p.meetingLink} onChange={v => update('meetingLink', v)} placeholder="Zoom / Meet / Teams recording or recurring link" />
              </Field>
            </Grid>

            {/* Document list with per-document upload */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Documents {docs.length > 0 && `(${docs.filter(d => d.status === 'verified').length}/${docs.length} verified)`}
                </div>
                <button onClick={() => setShowAddDocInline(s => !s)}
                  style={{ padding: '4px 12px', borderRadius: 20, border: '1px solid #7c3aed', background: 'rgba(124,58,237,0.08)', color: '#7c3aed', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  + Request Document
                </button>
              </div>

              {/* Inline quick-add panel */}
              {showAddDocInline && (
                <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 12, marginBottom: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>QUICK ADD COMMON DOCUMENTS</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                    {['Passport', 'Transcripts', 'Diploma / Marksheets', 'SOP / PS', 'LOR – Academic 1', 'LOR – Academic 2', 'LOR – Professional', 'CV / Resume', 'Bank Statement', 'IELTS/TOEFL Score', 'Visa Application Form', 'Offer Letter', 'Photo ID', 'Birth Certificate']
                      .filter(n => !docs.find(d => d.name === n))
                      .map(n => (
                        <button key={n} onClick={() => addDocToList(n)}
                          style={{ padding: '3px 10px', borderRadius: 20, border: '1px solid var(--border)', background: 'var(--bg-primary)', fontSize: 11, cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          + {n}
                        </button>
                      ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="crm-input" value={newDocName} onChange={e => setNewDocName(e.target.value)}
                      placeholder="Custom document name…" style={{ flex: 1, fontSize: 12 }}
                      onKeyDown={e => { if (e.key === 'Enter' && newDocName.trim()) { addDocToList(newDocName.trim()); setNewDocName(''); } }} />
                    <button onClick={() => { if (newDocName.trim()) { addDocToList(newDocName.trim()); setNewDocName(''); } }}
                      style={{ padding: '6px 14px', borderRadius: 8, background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Add</button>
                    <button onClick={() => setShowAddDocInline(false)}
                      style={{ padding: '6px 10px', borderRadius: 8, background: 'none', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>✕</button>
                  </div>
                </div>
              )}

              {/* Document rows */}
              {docs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)', fontSize: 12, border: '1px dashed var(--border)', borderRadius: 8 }}>
                  No documents requested yet — click "+ Request Document" to start the checklist
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                  {docs.map(docItem => (
                    <CompactDocRow key={docItem.id} docItem={docItem}
                      driveConfig={driveConfig} companyId={company?.id}
                      leadId={student?.id} currentUser={currentUser} />
                  ))}
                </div>
              )}
            </div>
          </Section>
        </div>

        {/* ── TRACKING ─── */}
        <div id="drawer-section-tracking">
          <Section title="Business Tracking" icon="📋">
            <Grid>
              <Field label="Enrolled Student ID"><Inp value={p.studentIdFinal} onChange={v => update('studentIdFinal', v)} placeholder="Final student ID at enrolled university" /></Field>
              <Field label="Visa Status">
                <Sel value={p.visaStatus} onChange={v => update('visaStatus', v)} options={VISA_STATUSES} placeholder="Select status" />
              </Field>
              <Field label="Total Scholarship Value"><Inp value={p.scholarshipTotal} onChange={v => update('scholarshipTotal', v)} placeholder="e.g. $45,000 / Full Ride" /></Field>
              <Field label="Partner Institution?">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!p.partnerSchool} onChange={e => update('partnerSchool', e.target.checked)} />
                  Yes — this is a partner/referral institution
                </label>
              </Field>
            </Grid>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
              {[
                { key: 'googleReview', label: '⭐ Google Review', icon: '⭐' },
                { key: 'hasPic',       label: '📸 Photo', icon: '📸' },
                { key: 'hasVideo',     label: '🎬 Video Testimonial', icon: '🎬' },
                { key: 'enrolled_confirmed', label: '🎓 Enrollment Confirmed', icon: '🎓' },
              ].map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, background: p[item.key] ? 'rgba(16,185,129,0.08)' : 'rgba(0,0,0,0.03)', border: `1px solid ${p[item.key] ? 'rgba(16,185,129,0.2)' : 'var(--border)'}` }}>
                  <input type="checkbox" checked={!!p[item.key]} onChange={e => update(item.key, e.target.checked)} />
                  <span style={{ fontWeight: 600 }}>{item.label}</span>
                </label>
              ))}
            </div>

            {/* Photo & Testimonial (auto-populated from student form) */}
            <div style={{ marginTop: 16, padding: 14, borderRadius: 12, background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.12)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 12 }}>📸 Photo & Testimonial</div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Photo */}
                <div style={{ flexShrink: 0 }}>
                  {p.photoUrl ? (
                    <a href={p.photoUrl} target="_blank" rel="noopener noreferrer">
                      <img src={p.photoUrl} alt="Student" style={{ width: 80, height: 96, objectFit: 'cover', borderRadius: 10, border: '2px solid rgba(16,185,129,0.3)', display: 'block' }} />
                    </a>
                  ) : (
                    <div style={{ width: 80, height: 96, borderRadius: 10, border: '2px dashed #cbd5e1', background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <span style={{ fontSize: 22 }}>📷</span>
                      <span style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>Awaiting student upload</span>
                    </div>
                  )}
                </div>
                {/* Testimonial fields */}
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Testimonial Text</div>
                    <textarea className="crm-input" rows={3} value={p.testimonialText || ''} onChange={e => update('testimonialText', e.target.value)} placeholder="Student's testimonial (filled by student or counselor)" style={{ resize: 'vertical', fontSize: 12, width: '100%' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Video Link</div>
                    <input className="crm-input" type="url" value={p.testimonialVideoUrl || ''} onChange={e => update('testimonialVideoUrl', e.target.value)} placeholder="YouTube / Drive video URL" style={{ fontSize: 12, width: '100%' }} />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Internal Comments / Notes" full>
                <textarea className="crm-input" rows={3} value={p.comments || ''} onChange={e => update('comments', e.target.value)} placeholder="Counselor notes, important dates, special circumstances…" style={{ resize: 'vertical', fontSize: 12 }} />
              </Field>
            </div>
          </Section>
        </div>

        {/* Save button at bottom */}
        <div style={{ padding: '16px 0', display: 'flex', gap: 10 }}>
          <button onClick={save}
            style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>
            {saving ? 'Saving…' : '✓ Save Student Profile'}
          </button>
          <button onClick={onClose}
            style={{ padding: '12px 20px', borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Filter Sidebar ─────────────────────────────────────────────────────────────
function FilterSidebar({ filters, setFilters, allStudents }) {
  const uniNames = useMemo(() => {
    const set = new Set();
    allStudents.forEach(s => (s.profile?.universities || []).forEach(u => u.name && set.add(u.name)));
    return [...set].sort();
  }, [allStudents]);

  const programs = useMemo(() => {
    const set = new Set();
    allStudents.forEach(s => (s.profile?.universities || []).forEach(u => u.program && set.add(u.program)));
    return [...set].sort();
  }, [allStudents]);

  const upd = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const clear = () => setFilters({});
  const hasFilters = Object.values(filters).some(v => v && v !== '');

  return (
    <div style={{ width: 228, borderRight: '1px solid var(--border)', background: '#f8fafc', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)' }}>FILTERS</span>
        {hasFilters && <button onClick={clear} style={{ fontSize: 10, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Clear All</button>}
      </div>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>SEARCH</div>
          <input className="crm-input" value={filters.search || ''} onChange={e => upd('search', e.target.value)} placeholder="Name, email, school…" style={{ fontSize: 11 }} />
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>APPLIED TO UNIVERSITY</div>
          <select className="crm-select" value={filters.university || ''} onChange={e => upd('university', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">All Universities</option>
            {uniNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>PROGRAM / COURSE</div>
          <input className="crm-input" value={filters.program || ''} onChange={e => upd('program', e.target.value)} placeholder="e.g. CS, MBA, Law…" style={{ fontSize: 11 }} />
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>UNIVERSITY STATUS</div>
          <select className="crm-select" value={filters.uniStatus || ''} onChange={e => upd('uniStatus', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">Any Status</option>
            {UNI_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>MAIN COUNSELOR</div>
          <select className="crm-select" value={filters.mainCounselor || ''} onChange={e => upd('mainCounselor', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">All</option>
            {MAIN_COUNSELORS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>COUNSELOR</div>
          <input className="crm-input" value={filters.counselor || ''} onChange={e => upd('counselor', e.target.value)} placeholder="Counselor name…" style={{ fontSize: 11 }} />
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>DEGREE</div>
          <select className="crm-select" value={filters.degree || ''} onChange={e => upd('degree', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">All Degrees</option>
            {DEGREE_OPTIONS.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>TARGET SEMESTER</div>
          <select className="crm-select" value={filters.semester || ''} onChange={e => upd('semester', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">All Semesters</option>
            {SEMESTER_OPTIONS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>VISA STATUS</div>
          <select className="crm-select" value={filters.visa || ''} onChange={e => upd('visa', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">All</option>
            {VISA_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5 }}>COUNTRY APPLIED</div>
          <select className="crm-select" value={filters.country || ''} onChange={e => upd('country', e.target.value)} style={{ width: '100%', fontSize: 11 }}>
            <option value="">All Countries</option>
            {COUNTRY_OPTIONS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 2 }}>QUICK FILTERS</div>
          {[
            { key: 'partnerOnly', label: 'Partner Schools Only' },
            { key: 'hasFolder',   label: 'Has Document Folder' },
            { key: 'hasReview',   label: 'Has Google Review' },
            { key: 'hasVideo',    label: 'Has Video Testimonial' },
            { key: 'visaApproved',label: 'Visa Approved' },
          ].map(f => (
            <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!filters[f.key]} onChange={e => upd(f.key, e.target.checked)} />
              {f.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Name helper — Facebook leads store firstName/lastName, web leads store name ──
function getLeadName(lead) {
  if (!lead) return '—';
  const first = lead.firstName || '';
  const last  = lead.lastName  || '';
  if (first || last) return (first + ' ' + last).trim();
  return lead.name || lead.email || lead.phone || '—';
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function Applications() {
  const { leads, currentUser } = useCRM();
  const companyId = currentUser?.companyId;

  const [profiles,          setProfiles]          = useState({});
  const [filters,           setFilters]           = useState({});
  const [selected,          setSelected]          = useState(null);
  const [view,              setView]              = useState('table'); // 'table' | 'detail'
  const [loading,           setLoading]           = useState(true);
  const [showOnlyWithApps,  setShowOnlyWithApps]  = useState(true);

  // Subscribe to all studentProfiles for this company
  useEffect(() => {
    if (!companyId) return;
    const q = collection(db, 'companies', companyId, 'studentProfiles');
    return onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      setProfiles(map);
      setLoading(false);
    });
  }, [companyId]);

  // Save profile
  const handleSave = async (leadId, profileData) => {
    await setDoc(
      doc(db, 'companies', companyId, 'studentProfiles', leadId),
      { ...profileData, updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  // Joined students
  const allStudents = useMemo(() => {
    return (leads || []).map(l => ({ ...l, profile: profiles[l.id] || {} }));
  }, [leads, profiles]);

  // Apply filters
  const filteredStudents = useMemo(() => {
    return allStudents.filter(s => {
      const p = s.profile || {};
      const unis = p.universities || [];

      // Default: only show students who have at least one university application
      if (showOnlyWithApps && unis.length === 0) return false;

      if (filters.search) {
        const q = filters.search.toLowerCase();
        const match = getLeadName(s).toLowerCase().includes(q) ||
          (s.email || '').toLowerCase().includes(q) ||
          (p.schoolName || '').toLowerCase().includes(q) ||
          (s.phone || '').includes(q);
        if (!match) return false;
      }

      if (filters.university) {
        if (!unis.some(u => u.name?.toLowerCase().includes(filters.university.toLowerCase()))) return false;
      }

      if (filters.program) {
        if (!unis.some(u => u.program?.toLowerCase().includes(filters.program.toLowerCase()) || (p.major || '').toLowerCase().includes(filters.program.toLowerCase()))) return false;
      }

      if (filters.uniStatus) {
        if (!unis.some(u => u.status === filters.uniStatus)) return false;
      }

      if (filters.country) {
        if (!unis.some(u => u.country === filters.country)) return false;
      }

      if (filters.mainCounselor && p.mainCounselor !== filters.mainCounselor) return false;
      if (filters.counselor && !(p.counselorName || s.assignedTo || '').toLowerCase().includes(filters.counselor.toLowerCase())) return false;
      if (filters.degree && p.degree !== filters.degree) return false;
      if (filters.semester && p.semesterApplied !== filters.semester) return false;
      if (filters.visa && p.visaStatus !== filters.visa) return false;

      if (filters.partnerOnly && !unis.some(u => u.partnerSchool)) return false;
      if (filters.hasFolder && !p.documentFolderUrl) return false;
      if (filters.hasReview && !p.googleReview) return false;
      if (filters.hasVideo && !p.hasVideo) return false;
      if (filters.visaApproved && !(p.visaStatus || '').includes('Approved')) return false;

      return true;
    });
  }, [allStudents, filters, showOnlyWithApps]);

  const handleSelect = (student) => {
    setSelected(student);
    setView('detail');
  };

  const handleBack = () => {
    setView('table');
    setSelected(null);
  };

  const handleSaveAndRefresh = async (leadId, profileData) => {
    await handleSave(leadId, profileData);
    // Optimistic update
    setProfiles(prev => ({ ...prev, [leadId]: profileData }));
  };

  // Summary stats
  const stats = useMemo(() => ({
    total: allStudents.length,
    filtered: filteredStudents.length,
    enrolled: allStudents.filter(s => (s.profile?.universities || []).some(u => u.status === 'enrolled')).length,
    accepted: allStudents.filter(s => (s.profile?.universities || []).some(u => u.status === 'accepted')).length,
    visaApproved: allStudents.filter(s => (s.profile?.visaStatus || '').includes('Approved')).length,
  }), [allStudents, filteredStudents]);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', flexDirection: 'column', background: 'var(--bg-secondary)' }}>

      {/* Top bar */}
      <div style={{ padding: '12px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {view === 'detail' && (
            <button onClick={handleBack}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              ← Back to Table
            </button>
          )}
          <div style={{ fontSize: 16, fontWeight: 800 }}>
            {view === 'detail' && selected ? getLeadName(selected) : 'Student Applications'}
          </div>
          {view === 'table' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{filteredStudents.length}</span> of {stats.total} students
              </div>
              <button onClick={() => setShowOnlyWithApps(v => !v)}
                style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, borderRadius: 20, cursor: 'pointer', border: `1px solid ${showOnlyWithApps ? '#7c3aed' : 'var(--border)'}`, background: showOnlyWithApps ? 'rgba(124,58,237,0.1)' : 'transparent', color: showOnlyWithApps ? '#7c3aed' : 'var(--text-muted)' }}>
                {showOnlyWithApps ? '🎓 Applied Only' : '👥 All Leads'}
              </button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Stats summary */}
          {view === 'table' && (
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { label: 'Total', val: stats.total, color: '#7c3aed' },
                { label: 'Accepted', val: stats.accepted, color: '#10b981' },
                { label: 'Enrolled', val: stats.enrolled, color: '#059669' },
                { label: 'Visa ✓', val: stats.visaApproved, color: '#3b82f6' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '4px 12px', background: s.color + '10', borderRadius: 8, border: '1px solid ' + s.color + '25' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {view === 'table' && (
          <>
            <FilterSidebar filters={filters} setFilters={setFilters} allStudents={allStudents} />
            <MasterTable
              students={filteredStudents}
              onSelect={handleSelect}
              selectedId={selected?.id}
              loading={loading}
            />
          </>
        )}

        {view === 'detail' && selected && (
          <StudentDrawer
            student={selected}
            profile={profiles[selected.id] || {}}
            onClose={handleBack}
            onSave={handleSaveAndRefresh}
          />
        )}
      </div>
    </div>
  );
}
